import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import customerService from '../../../utils/customerService';
import reviewService from '../../../utils/reviewService'; // Import reviewService

const CustomerDetailModal = ({ isOpen, onClose, customer, onEdit, onSendReviewRequest, onViewReviews }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (isOpen && customer?.id && activeTab === 'history') {
      loadFeedbackHistory();
    }
  }, [isOpen, customer?.id, activeTab]);

  const loadFeedbackHistory = async () => {
    setHistoryLoading(true);
    try {
      // This method will need to be created in reviewService
      const result = await reviewService.getReviewHistoryByContact(customer.id);
      if (result.success) {
        setFeedbackHistory(result.data);
      }
    } catch (error) {
      console.error('Error loading feedback history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getCustomerIcon = () => customer.customer_type === 'organization' ? 'Building' : 'User';
  
  const getCustomerDisplayName = () => {
    if (customer.customer_type === 'organization' && customer.organization_name) {
      return customer.organization_name;
    }
    return customer.name;
  };

  const formatDate = (dateString, includeTime = false) => {
    if (!dateString) return 'N/A';
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">Completed</span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs font-medium text-yellow-700 bg-yellow-100 rounded-full">Pending</span>;
      case 'sent':
        return <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">Sent</span>;
      case 'expired':
        return <span className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">Expired</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">{status}</span>;
    }
  };

  if (!isOpen || !customer) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <Icon name={getCustomerIcon()} size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">{getCustomerDisplayName()}</h2>
              {customer.customer_type === 'organization' && customer.organization_name && (
                <p className="text-text-secondary">Contact: {customer.name}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {onEdit && (
              <Button variant="outline" onClick={() => onEdit(customer)}>
                <Icon name="Edit" size={16} className="mr-2" />
                Edit
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border px-6 flex-shrink-0">
          <nav className="flex space-x-4">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`py-3 px-1 text-sm font-medium ${activeTab === 'overview' ? 'border-b-2 border-primary text-primary' : 'text-text-muted hover:text-foreground'}`}
            >
              Overview
            </button>
            <button 
              onClick={() => setActiveTab('history')}
              className={`py-3 px-1 text-sm font-medium ${activeTab === 'history' ? 'border-b-2 border-primary text-primary' : 'text-text-muted hover:text-foreground'}`}
            >
              Feedback Timeline
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Contact Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Icon name="Mail" size={16} className="text-text-muted" />
                      <span className="text-foreground">{customer.email || 'N/A'}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="Phone" size={16} className="text-text-muted" />
                      <span className="text-foreground">{customer.phone || 'N/A'}</span>
                    </div>
                    {customer.address && (
                      <div className="flex items-start space-x-3">
                        <Icon name="MapPin" size={16} className="text-text-muted mt-0.5" />
                        <span className="text-foreground">{`${customer.address}, ${customer.city}, ${customer.state}`}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Other details can go here */}
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              {historyLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-text-secondary">Loading feedback history...</p>
                </div>
              ) : feedbackHistory.length > 0 ? (
                <div className="space-y-4">
                  {feedbackHistory.map(item => (
                    <div key={item.id} className="p-4 bg-card-hover rounded-lg border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold text-foreground">
                          Request Sent: {formatDate(item.sent_at, true)}
                        </div>
                        {getStatusBadge(item.status)}
                      </div>
                      {item.review ? (
                        <div className="p-3 bg-card rounded border-l-4 border-green-500">
                          <div className="flex items-center space-x-2 mb-1">
                            {[...Array(5)].map((_, i) => (
                              <Icon 
                                key={i} 
                                name="Star" 
                                size={16} 
                                className={i < item.review.rating ? 'text-yellow-400' : 'text-gray-300'} 
                                fill={i < item.review.rating ? 'currentColor' : 'none'}
                              />
                            ))}
                          </div>
                          <p className="text-sm text-text-secondary italic">"{item.review.comment}"</p>
                          <p className="text-xs text-text-muted mt-2">Reviewed on: {formatDate(item.review.submitted_at)}</p>
                        </div>
                      ) : (
                        <p className="text-sm text-text-muted">No review submitted for this request.</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon name="MessageSquare" size={48} className="text-text-muted mx-auto mb-4" />
                  <h4 className="font-semibold text-foreground">No Feedback History</h4>
                  <p className="text-text-secondary">This customer has not been sent any review requests yet.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailModal; 